import { useCallback, useMemo, useState } from 'react';
import { CommonModal, CustomSvg, usePortkeyAsset } from '..';
import { NetworkType } from '../../types';
import { CaAddressInfosType, IAssetToken } from '@portkey/services';
import { TokenType } from '../types/assets';
import { INftInfoType } from '@portkey/types';
import { basicAssetViewAsync } from '../context/PortkeyAssetProvider/actions';
import { MAINNET } from '../../constants/network';
import CommonTabs from '../CommonTabs';
import SendTokenList from './TokenList';
import SendNFTList from './NFTList';
import CommonInput from '../CommonInput';
import TitleWrapper from '../TitleWrapper';
import { useEffectOnce } from 'react-use';
import { usePortkey } from '../context';
import './index.less';

export interface ISendAssetListProps {
  networkType: NetworkType;
  caAddressInfos?: CaAddressInfosType;
  onSelect: (v: IAssetToken | INftInfoType, type: TokenType) => void;
  mode?: 'dark' | 'light';
}

export enum SendAssetTabEnum {
  TOKEN = 'TOKED',
  NFT = 'NFT',
}

export default function SendAssetList({ networkType, caAddressInfos, onSelect }: ISendAssetListProps) {
  const [{ allAssetV2 }, { dispatch }] = usePortkeyAsset();
  const [filterWord, setFilterWord] = useState<string>('');
  const isMainnet = useMemo(() => networkType === MAINNET, [networkType]);
  const [curTab, setCurTab] = useState<SendAssetTabEnum>(SendAssetTabEnum.TOKEN);
  const [fetching, setFetching] = useState(false);

  const onSearch = useCallback(
    (v: string) => {
      setFilterWord(v);
      if (!caAddressInfos) return;
      setFetching(true);
      basicAssetViewAsync
        .setAllAssetInfoV2({
          keyword: v,
          caAddressInfos,
        })
        .then(dispatch)
        .finally(() => {
          setFetching(false);
        });
    },
    [caAddressInfos, dispatch],
  );

  useEffectOnce(() => {
    onSearch('');
  });

  const noDataMessage = useMemo(() => {
    return filterWord ? 'No results found' : 'There are currently no assets to send.';
  }, [filterWord]);

  return (
    <div className="portkey-ui-send-asset-list">
      <CommonInput
        type="search"
        placeholder="Search"
        value={filterWord}
        onChange={(e) => {
          const v = e.target.value.trim();
          onSearch(v);
        }}
        className="portkey-ui-send-search"
        onClear={() => {
          onSearch('');
        }}
      />
      <CommonTabs
        className="portkey-ui-send-asset"
        activeKey={curTab}
        onChange={(v) => {
          setCurTab(v);
        }}
        items={[
          {
            label: 'Tokens',
            key: SendAssetTabEnum.TOKEN,
            children: (
              <SendTokenList
                onSelect={(v) => onSelect(v, 'TOKEN')}
                tokenInfos={allAssetV2?.tokenInfos || []}
                loading={fetching}
                noDataMessage={noDataMessage}
                isMainnet={isMainnet}
              />
            ),
          },
          {
            label: 'NFTs',
            key: SendAssetTabEnum.NFT,
            children: (
              <SendNFTList
                onSelect={(v) => onSelect(v, 'NFT')}
                nftInfos={allAssetV2?.nftInfos || []}
                loading={fetching}
                noDataMessage={noDataMessage}
                isMainnet={isMainnet}
              />
            ),
          },
        ]}
      />
    </div>
  );
}

export function SendAssetListPage({ onCancel, ...props }: ISendAssetListProps & { onCancel: () => void }) {
  return (
    <div className="portkey-ui-send-asset-list-page">
      <div className="send-select-asset-header portkey-ui-flex-between-center">
        <CustomSvg
          type="ArrowLeft"
          fillColor="var(--sds-color-icon-default-default)"
          className="portkey-ui-cursor-pointer"
          onClick={onCancel}
        />
        <div>{`Select asset to send`}</div>
        <div></div>
      </div>
      <SendAssetList {...props} />
    </div>
  );
}

export function SendAssetListModal({
  open,
  onCancel,
  ...props
}: ISendAssetListProps & { open: boolean; onCancel: () => void }) {
  const [{ theme: providerTheme }] = usePortkey();
  const isDarkMode = useMemo(() => (props.mode || providerTheme) === 'dark', [props.mode, providerTheme]);
  return (
    <CommonModal open={open} type="modal" className="send-select-asset-modal">
      <TitleWrapper
        className="send-select-asset-header"
        leftElement={<div className="header-title">{`Select asset to send`}</div>}
        rightElement={<CustomSvg type="Close" className={isDarkMode ? '' : 'light-mode'} onClick={onCancel} />}
      />
      <SendAssetList {...props} />
    </CommonModal>
  );
}
